import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { PointsRuleList } from "@/components/campaigns/tournament/how-to-get-points-card";
import { TournamentPrizeBoard } from "@/components/campaigns/tournament/tournament-prize-board";
import { Badge } from "@/components/ui/badge";
import { RichText } from "@/components/ui/rich-text";
import { cn } from "@/lib/utils";
import type { CampaignSchema, PrizeSchema, TaskSchema } from "@/types";
import { campaignBodyHtml, getTermsContent } from "@/utils/campaign-helpers";
import { getLanguageCookie } from "@/utils/cookies";
import { currencySymbolFirst } from "@/utils/format-money";
import {
  listMatches,
  parseTermsBlocks,
  plainText,
  termsSegments,
  type TermsList,
} from "@/utils/terms-blocks";

import { InfoSection, SectionLabel } from "./campaign-sections";
import { CARD_BODY_TEXT } from "./card-body-text";

type TermsFacts = {
  minimumBet: string | null;
  dates: string[];
  /** The campaign's jackpot names, as `plainText` lower-cased: a terms list led by one is a pot. */
  pots: string[];
};

/** One rule: its sentence, and any list that belongs to it (the sentence just before a list is
 *  its lead-in — "Below you can see how many points each task gives:"). */
type TermsItem = { html?: string; lists: TermsList[] };

/** A run of the T&C laid out as one card: plain rules, or one of the campaign's own sections. */
type TermsSection =
  | { kind: "rules"; items: TermsItem[] }
  | { kind: "points"; lead?: string; tasks: TaskSchema[] }
  | { kind: "prizes"; lead?: string; prizes: PrizeSchema[] };

/**
 * The T&C tab, built from the backend's flat terms text (see `utils/terms-blocks`) with the
 * widget's own pieces, all in ONE "T&C" card: the rules as rows, and the scoring and prize lists
 * — when they match the campaign's data — as labelled sections inside it with the components the
 * Overview and Info tabs use ("How to get points" rows, the prize board), in the order the terms
 * give them. The minimum bet and the dates read as text badges. Terms an operator wrote with real
 * structure (lists, tables) render as authored, in one card.
 */
export const CampaignTermsSections = ({
  campaign,
}: {
  campaign: CampaignSchema;
}) => {
  const { t } = useTranslation();
  const raw = getTermsContent(
    campaign.terms as unknown as Record<string, unknown> | undefined,
    getLanguageCookie()
  );
  const html = raw?.trim() ? campaignBodyHtml(raw) : "";
  const sections = useMemo(
    () => (html ? buildSections(html, campaign) : null),
    [html, campaign]
  );
  const termsTitle = t("campaign.inforTabs.termsAndConditions", "T&C");

  if (!html || !sections) {
    return (
      <InfoSection title={termsTitle}>
        {html ? (
          <RichText body={raw} className={CARD_BODY_TEXT} />
        ) : (
          <p className={cn("w-full font-body", CARD_BODY_TEXT)}>
            {t("general.noTermsAvailable")}
          </p>
        )}
      </InfoSection>
    );
  }

  // The values Unibo's template fills in, taken from the campaign itself: only these get styled.
  const facts: TermsFacts = {
    minimumBet: campaign.minimum_bet?.display ?? null,
    dates: [campaign.start, campaign.end].filter(
      (d): d is string => typeof d === "string"
    ),
    pots: (campaign.jackpots ?? []).map((j) =>
      plainText(j.title).toLowerCase()
    ),
  };
  // ONE card: the rules, with the scoring and prize lists as labelled sections INSIDE it (divided
  // by the card's hairline), in the order the terms give them — the T&C is one document, not a
  // stack of cards.
  return (
    <InfoSection title={termsTitle}>
      <div className="flex w-full flex-col">
        {sections.map((section, i) => {
          const divided = i > 0 && "border-t border-foreground/[0.06] pt-3";
          if (section.kind === "points") {
            return (
              <div key={i} className={cn("flex flex-col gap-2 py-3", divided)}>
                <SectionLabel>
                  {t("tournament.howToGetPoints", "How to get points")}
                </SectionLabel>
                {section.lead && <RuleText html={section.lead} facts={facts} />}
                <PointsRuleList tasks={section.tasks} />
              </div>
            );
          }
          if (section.kind === "prizes") {
            return (
              <div key={i} className={cn("flex flex-col gap-2 py-3", divided)}>
                <SectionLabel>
                  {t("tournament.prizesHeading", "Prizes")}
                </SectionLabel>
                {section.lead && <RuleText html={section.lead} facts={facts} />}
                <TournamentPrizeBoard prizes={section.prizes} />
              </div>
            );
          }
          return (
            <div key={i} className={cn("py-3 first:pt-0 last:pb-0", divided)}>
              <RuleRows items={section.items} facts={facts} />
            </div>
          );
        })}
      </div>
    </InfoSection>
  );
};

/** Blocks → rules, with the lists that name the campaign's tasks or prizes lifted into their own
 *  sections (their lead-in sentence goes with them). */
const buildSections = (
  html: string,
  campaign: CampaignSchema
): TermsSection[] | null => {
  const blocks = parseTermsBlocks(html);
  if (!blocks) return null;
  const items: TermsItem[] = [];
  for (const block of blocks) {
    if (block.kind === "text") items.push({ html: block.html, lists: [] });
    else if (items.length) items[items.length - 1].lists.push(block);
    else items.push({ lists: [block] });
  }

  const prizes = (campaign.prizes ?? []).filter((p) => p.place_from != null);
  // Only a campaign that scores points has a "How to get points" table: a chase's targets
  // arrive with `points: 0`, and lifting them into that table would caption them "+0 pts" —
  // they stay the operator's plain list under their rule, as the backoffice preview shows them.
  const pointTasks = (campaign.tasks ?? []).filter(
    (task) => task.points != null
  );
  const tasks = pointTasks.some((task) => (task.points ?? 0) > 0)
    ? pointTasks
    : [];
  const sections: TermsSection[] = [];
  const rules = (): TermsItem[] => {
    const last = sections[sections.length - 1];
    if (last?.kind === "rules") return last.items;
    const next: TermsSection = { kind: "rules", items: [] };
    sections.push(next);
    return next.items;
  };

  for (const item of items) {
    const list = item.lists[0];
    if (
      item.lists.length === 1 &&
      prizes.length &&
      listMatches(
        list,
        prizes.map((p) => p.title)
      )
    ) {
      sections.push({ kind: "prizes", lead: item.html, prizes });
    } else if (
      item.lists.length === 1 &&
      tasks.length &&
      listMatches(
        list,
        tasks.map((task) => task.title)
      )
    ) {
      sections.push({ kind: "points", lead: item.html, tasks });
    } else {
      rules().push(item);
    }
  }
  return sections;
};

/** The rules of a section as rows divided by the cards' hairline, any other list under its rule. */
const RuleRows = ({
  items,
  facts,
}: {
  items: TermsItem[];
  facts: TermsFacts;
}) => (
  <div className="flex w-full flex-col">
    {items.map((item, i) => {
      const pots = item.lists.filter((list) => namesPot(list, facts.pots));
      const others = item.lists.filter((list) => !pots.includes(list));
      return (
        <div
          key={i}
          className={cn(
            "flex flex-col gap-2 py-2.5 first:pt-0 last:pb-0",
            i > 0 && "border-t border-foreground/[0.06]"
          )}
        >
          {item.html && <RuleText html={item.html} facts={facts} />}
          {pots.length > 0 && <PotPanel pots={pots} />}
          {/* A list that is not one of the campaign's own sections is the operator's text: its
              lines stay plain, under the rule they belong to. */}
          {others.map((list, j) => (
            <span
              key={j}
              className={cn("block w-full font-body", CARD_BODY_TEXT)}
            >
              {list.lines.map((line, k) => (
                <Fragment key={k}>
                  {k > 0 && <br />}
                  {plainText(line)}
                </Fragment>
              ))}
            </span>
          ))}
        </div>
      );
    })}
  </div>
);

/** A list whose first line is one of the campaign's jackpot names: the template's pot block. */
const namesPot = (list: TermsList, pots: string[]) =>
  pots.includes(plainText(list.lines[0] ?? "").toLowerCase());

/**
 * The jackpots Unibo's template lists (name, start value, the rule that drops it) as one faint
 * panel inside the T&C card — no border, a lifted surface — so the generated part reads as data,
 * not prose: the name leads, "Start value: €500" is a label and its figure, the rule a quieter
 * line under it.
 */
const PotPanel = ({ pots }: { pots: TermsList[] }) => (
  <div className="flex w-full flex-col rounded-(--border-radius-inner) bg-foreground/[0.04] px-3 lg:px-4">
    {pots.map((pot, i) => (
      <div
        key={i}
        className={cn(
          "flex flex-col gap-1 py-2.5 lg:py-3",
          i > 0 && "border-t border-foreground/[0.06]"
        )}
      >
        <p className="font-heading text-[13px] leading-[18px] font-bold text-foreground lg:text-[15px] lg:leading-5">
          {plainText(pot.lines[0])}
        </p>
        {pot.lines.slice(1).map((line, k) => (
          <PotLine key={k} text={plainText(line)} />
        ))}
      </div>
    ))}
  </div>
);

/** "Start value: €500" — a short label (no digits, so a clock time is never split) and a value. */
const LABEL_VALUE = /^([^:\d]{1,40}):\s*(.+)$/;
/** A money figure: a currency symbol on either side of the number, or a leading code. */
const MONEY = /(\p{Sc}\s?\d[\d.,]*|\d[\d.,]*\s?\p{Sc}|\b[A-Z]{3}\s\d[\d.,]*)/u;

const PotLine = ({ text }: { text: string }) => {
  const pair = text.match(LABEL_VALUE);
  if (pair) {
    return (
      <p
        className={cn(
          "flex w-full items-baseline justify-between gap-3 font-body",
          CARD_BODY_TEXT
        )}
      >
        <span className="min-w-0">{pair[1]}</span>
        <span className="shrink-0 text-right">
          <Amounts text={pair[2]} />
        </span>
      </p>
    );
  }
  return (
    <p
      className={cn(
        "w-full font-body",
        CARD_BODY_TEXT,
        "text-muted-foreground"
      )}
    >
      <Amounts text={text} />
    </p>
  );
};

/** Text with its money figures set as the prize-coloured text badge, symbol first ("€500"). */
const Amounts = ({ text }: { text: string }) => (
  <>
    {text.split(MONEY).map((part, i) =>
      i % 2 === 1 ? (
        <Badge key={i} variant="text" color="prizes" className={INLINE_BADGE}>
          {currencySymbolFirst(part)}
        </Badge>
      ) : (
        <Fragment key={i}>{part}</Fragment>
      )
    )}
  </>
);

/** A text badge set inline in running copy: the copy's size and leading, no padding. */
const INLINE_BADGE =
  "inline px-0 py-0 align-baseline text-[length:inherit] leading-[inherit] font-bold whitespace-nowrap lg:px-0 lg:py-0";

/**
 * One rule's sentence. Plain sentences get the minimum bet and the dates as the widget's text
 * badges; a sentence with its own inline markup (an operator's bold, a link) renders as written.
 */
const RuleText = ({
  html,
  facts,
  className,
}: {
  html: string;
  facts: TermsFacts;
  className?: string;
}) => {
  const classes = cn("block w-full font-body", CARD_BODY_TEXT, className);
  if (/<[a-z][^>]*>/i.test(html)) {
    return (
      <span className={classes} dangerouslySetInnerHTML={{ __html: html }} />
    );
  }
  const text = plainText(html.replace(/[,;]+$/, "")) + trailingStop(html);
  return (
    <span className={classes}>
      {termsSegments(text, facts).map((segment, i) =>
        segment.kind === "text" ? (
          <Fragment key={i}>{segment.value}</Fragment>
        ) : (
          <Badge
            key={i}
            variant="text"
            color={segment.kind === "bet" ? "prizes" : "foreground"}
            className={INLINE_BADGE}
          >
            {segment.value}
          </Badge>
        )
      )}
    </span>
  );
};

/** `plainText` drops a closing full stop for matching; a sentence keeps its own. */
const trailingStop = (html: string) => (/\.\s*$/.test(html) ? "." : "");
